import * as Dialog from "@radix-ui/react-dialog";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Mail, MoreHorizontal, Plus, ShieldCheck, Trash2, UserRound, Users } from "lucide-react";
import { areaLevelSummary, choiceLabel, PermissionsDialog, PermissionsPicker, toRolePermissions, type Choice } from "@/components/PermissionsEditor";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, PERMISSION_PRESETS, effectivePermissions, type Permissions, type TenantRole } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

export const ROLE_INFO: Record<TenantRole, { label: string; text: string }> = {
  customer_admin: { label: msg("Administrator"), text: msg("Alles, inkl. Shops, Regeln und Team") },
  customer_user: { label: msg("Mitarbeiter"), text: msg("Artikel, Aufträge, Bestände, Wareneingänge") },
};

const errorText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Aktion fehlgeschlagen") : "Aktion fehlgeschlagen");

export function TeamPage() {
  const { tenantId, tenant, me } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["team", tenantId], queryFn: () => api.team.get(tenantId) });
  const [inviteOpen, setInviteOpen] = useState(false);
  const manage = canManage(q.data?.myRole);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["team", tenantId] });

  const [editing, setEditing] = useState<NonNullable<typeof q.data>["members"][number] | null>(null);
  const remove = useMutation({ mutationFn: (userId: string) => api.team.remove(tenantId, userId), onSuccess: () => { toast.success("Zugang entfernt – die Person ist sofort abgemeldet"); refresh(); }, onError: (e) => toast.error(errorText(e)) });
  const revoke = useMutation({ mutationFn: (id: string) => api.team.revokeInvite(tenantId, id), onSuccess: () => { toast.success("Einladung zurückgezogen"); refresh(); } });

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Team")}
        description={t("Wer bei {name} Zugriff auf das Portal hat.", { name: tenant.name })}
        actions={manage ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setInviteOpen(true)}>{t("Person einladen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6">
        <Card className="overflow-hidden min-w-0">
          {q.isLoading && <div className="p-4 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>}
          <ul className="divide-y divide-border">
            {q.data?.members.map((m) => {
              const isMe = m.email === me.user.email;
              return (
                <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
                  <span className="size-9 rounded-full bg-surface-2 border border-border grid place-items-center font-semibold text-[13px]">{(m.name ?? m.email).slice(0, 1).toUpperCase()}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.name ?? m.email} {isMe && <span className="text-fg-subtle font-normal">{t("(du)")}</span>}</div>
                    <div className="text-[12px] text-fg-muted truncate">{m.name ? `${m.email} · ` : ""}{m.lastLoginAt ? t("zuletzt aktiv {v}", { v: fmtRelative(m.lastLoginAt) }) : t("noch nie angemeldet")}</div>
                    {m.role !== "customer_admin" && m.permissions && <div className="text-[11.5px] text-fg-subtle truncate" title={areaLevelSummary(m.permissions)}>{areaLevelSummary(m.permissions) || t("kein Zugriff auf Bereiche")}</div>}
                  </div>
                  <Badge tone={m.role === "customer_admin" ? "coral" : "neutral"}>{choiceLabel(m.role === "customer_admin" ? "admin" : m.preset)}</Badge>
                  {manage && (
                    <Dropdown.Root>
                      <Dropdown.Trigger className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Aktionen für {email}", { email: m.email })}><MoreHorizontal className="size-4" /></Dropdown.Trigger>
                      <Dropdown.Portal>
                        <Dropdown.Content align="end" sideOffset={4} className="z-50 w-60 rounded-xl border border-border bg-surface p-1 shadow-xl animate-pop-in">
                          <Dropdown.Item onSelect={() => setEditing(m)} className="flex items-center gap-2 h-8 px-2 rounded-lg outline-none cursor-pointer data-[highlighted]:bg-surface-2">
                            <KeyRound className="size-4" /> {t("Rolle & Rechte …")}
                          </Dropdown.Item>
                          <Dropdown.Separator className="h-px bg-border my-1" />
                          <Dropdown.Item onSelect={() => { if (confirm(`Zugang von ${m.email} wirklich entfernen?`)) remove.mutate(m.userId); }} className="flex items-center gap-2 h-8 px-2 rounded-lg outline-none cursor-pointer text-danger data-[highlighted]:bg-danger-soft">
                            <Trash2 className="size-4" /> {t("Zugang entfernen")}
                          </Dropdown.Item>
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  )}
                </li>
              );
            })}
            {q.data?.invites.map((i) => (
              <li key={i.id} className="flex items-center gap-3 px-4 py-3 bg-surface-2/40">
                <span className="size-9 rounded-full border border-dashed border-border-strong grid place-items-center text-fg-subtle"><Mail className="size-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{i.email}</div>
                  <div className="text-[12px] text-fg-muted">{t("Eingeladen")} {fmtRelative(i.createdAt)} {t("· gültig bis")} {new Date(i.expiresAt).toLocaleDateString(intlLocale())}</div>
                </div>
                <Badge tone="info">{t("Einladung offen")}</Badge>
                {manage && <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)}>{t("Zurückziehen")}</Button>}
              </li>
            ))}
          </ul>
          {q.data && q.data.members.length === 0 && q.data.invites.length === 0 && (
            <EmptyState icon={<Users className="size-5" />} title={t("Noch niemand im Team")} action={manage ? <Button variant="primary" onClick={() => setInviteOpen(true)}>{t("Erste Person einladen")}</Button> : undefined} />
          )}
        </Card>
        <Card className="p-4 h-fit">
          <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="size-4 text-fg-subtle" /> {t("Rollen")}</h3>
          <dl className="mt-3 space-y-3 text-[12.5px]">
            <div><dt className="font-medium">{t("Administrator")}</dt><dd className="text-fg-muted">{t("Alles, inkl. Shops, Regeln, Team und Module")}</dd></div>
            {Object.entries(PERMISSION_PRESETS).map(([k, v]) => (
              <div key={k}><dt className="font-medium">{t(v.label)}</dt><dd className="text-fg-muted">{t(v.description)}</dd></div>
            ))}
            <div><dt className="font-medium">{t("Individuell")}</dt><dd className="text-fg-muted">{t("Je Bereich: bearbeiten, ansehen oder kein Zugriff")}</dd></div>
          </dl>
          <p className="mt-4 text-[12px] text-fg-subtle">{t("Anmeldung immer per E-Mail-Link – ohne Passwort. Entfernte Personen werden sofort abgemeldet.")}</p>
        </Card>
      </div>
      <CustomerInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvite={async (email, v) => { await api.team.invite(tenantId, email, v.role, v.permissions ?? undefined); refresh(); }} />
      {editing && (
        <PermissionsDialog
          member={editing}
          onClose={() => setEditing(null)}
          onSave={async (v) => {
            try {
              await api.team.update(tenantId, editing.userId, v);
              toast.success("Rolle & Rechte gespeichert – gelten sofort");
              refresh();
            } catch (e) {
              toast.error(errorText(e));
              throw e;
            }
          }}
        />
      )}
    </div>
  );
}

export function InviteDialog<R extends string = TenantRole>({
  open,
  onOpenChange,
  onInvite,
  roles = ROLE_INFO as unknown as Record<R, { label: string; text: string }>,
  defaultRole = "customer_user" as R,
  title = "Person einladen",
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onInvite: (email: string, role: R) => Promise<void>;
  roles?: Record<R, { label: string; text: string }>;
  defaultRole?: R;
  title?: string;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<R>(defaultRole);
  const [busy, setBusy] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onInvite(email.trim().toLowerCase(), role);
      toast.success(`Einladung an ${email.trim()} verschickt`, { description: msg("Der Link ist 7 Tage gültig.") });
      setEmail("");
      onOpenChange(false);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(460px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{t(title)}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1">{t("Die Person bekommt eine E-Mail mit einem Link zum Portal.")}</Dialog.Description>
          <label className="block mt-5">
            <span className="text-[12px] font-medium text-fg-muted">{t("E-Mail-Adresse")}</span>
            <Input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} placeholder={t("name@firma.de")} className="mt-1" leading={<UserRound className="size-3.5" />} />
          </label>
          <div className="mt-4 space-y-2" role="radiogroup" aria-label={t("Rolle")}>
            {(Object.entries(roles) as [R, { label: string; text: string }][]).map(([k, v]) => (
              <button key={k} role="radio" aria-checked={role === k} onClick={() => setRole(k)} className={cx("w-full flex items-start gap-3 p-3 rounded-xl border text-left", role === k ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
                <span className={cx("mt-0.5 size-4 rounded-full border-2 grid place-items-center shrink-0", role === k ? "border-ink" : "border-border-strong")}>{role === k && <span className="size-2 rounded-full bg-ink" />}</span>
                <span><span className="block font-medium text-[13px]">{t(v.label)}</span><span className="block text-[12px] text-fg-muted">{t(v.text)}</span></span>
              </button>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!valid} loading={busy} onClick={() => void submit()}>{t("Einladung senden")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Kunden-Team: einladen mit Rolle bzw. Vorlage (Rechte je Bereich) */
function CustomerInviteDialog({ open, onOpenChange, onInvite }: { open: boolean; onOpenChange: (o: boolean) => void; onInvite: (email: string, v: { role: TenantRole; permissions: import("@/lib/api").StoredPermissions | null }) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [choice, setChoice] = useState<Choice>("member");
  const [custom, setCustom] = useState<Permissions>(effectivePermissions("customer_user"));
  const [busy, setBusy] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onInvite(email.trim().toLowerCase(), toRolePermissions(choice, custom));
      toast.success(`Einladung an ${email.trim()} verschickt`, { description: msg("Der Link ist 7 Tage gültig.") });
      setEmail("");
      onOpenChange(false);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,calc(100vw-32px))] max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{t("Person einladen")}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1">{t("Die Person bekommt eine E-Mail mit einem Link zum Portal.")}</Dialog.Description>
          <label className="block mt-5">
            <span className="text-[12px] font-medium text-fg-muted">{t("E-Mail-Adresse")}</span>
            <Input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} placeholder={t("name@firma.de")} className="mt-1" leading={<UserRound className="size-3.5" />} />
          </label>
          <div className="mt-4"><PermissionsPicker choice={choice} onChoice={setChoice} custom={custom} onCustom={setCustom} /></div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!valid} loading={busy} onClick={() => void submit()}>{t("Einladung senden")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
